import { CustomDatePicker, CustomSelect } from '../common';
import { getPurchaseOrderQuantityStep } from '../../hooks/purchase-orders/purchaseOrderHelpers';

function PurchaseOrderForm({
  values,
  errors,
  supplierOptions,
  seaweedTypeOptions,
  warehouseLocationOptions = [],
  stockUnits,
  statusOptions,
  isSaving,
  canCreatePurchaseOrder,
  onChange,
  onSubmit,
  onCancel,
}) {
  return (
    <form
      className="rounded-[24px] border border-[var(--theme-border-soft)] bg-[linear-gradient(180deg,var(--theme-surface-warm)_0%,var(--theme-surface-muted)_100%)] p-5 shadow-[0_10px_24px_rgba(var(--theme-shadow-rgb),0.03)] sm:p-6"
      onSubmit={onSubmit}
      noValidate
    >
      <div className="mb-6 border-b border-[var(--theme-border-soft)] pb-5">
        <p className="m-0 text-[0.78rem] font-semibold uppercase tracking-[0.09em] text-[var(--theme-text-muted)]">
          Purchase Order Module
        </p>
        <h2 className="m-0 mt-2 text-[1.2rem] font-semibold tracking-[-0.03em] text-[var(--theme-text)] sm:text-[1.35rem]">
          Add Purchase Order
        </h2>
        <p className="m-0 mt-2 text-[0.95rem] text-[var(--theme-text-muted)]">
          Create a supplier order record for seaweeds delivery planning and tracking.
        </p>
      </div>

      <section className="rounded-[22px] border border-[var(--theme-border-soft)] bg-white p-4 shadow-[0_8px_18px_rgba(var(--theme-shadow-rgb),0.025)] sm:p-5">
        <div className="mb-4">
          <h3 className="m-0 text-[1rem] font-semibold tracking-[-0.03em] text-[var(--theme-text)]">
            Order Details
          </h3>
          <p className="m-0 mt-2 text-[0.9rem] text-[var(--theme-text-muted)]">
            Capture the supplier and expected delivery date in one place.
          </p>
        </div>

        <div className="grid items-start gap-3 md:grid-cols-2 xl:grid-cols-3">
          <FormSelect
            label="Supplier"
            name="supplierId"
            value={values.supplierId}
            error={errors.supplierId}
            onChange={onChange}
            options={supplierOptions}
            placeholder="Select supplier"
          />
          <FormInput
            label="Sacks Mark"
            name="sacksMark"
            value={values.sacksMark}
            error={errors.sacksMark}
            onChange={onChange}
            placeholder="Enter sacks mark"
          />
          <FormSelect
            label="Warehouse"
            name="warehouseLocation"
            value={values.warehouseLocation}
            error={errors.warehouseLocation}
            onChange={onChange}
            options={warehouseLocationOptions}
            placeholder="Select warehouse"
            disabled={!warehouseLocationOptions.length}
          />
          <FormSelect
            label="Unit"
            name="stockUnit"
            value={values.stockUnit}
            error={errors.stockUnit}
            onChange={onChange}
            options={stockUnits}
          />
          <FormInput
            label="Quantity"
            name="quantity"
            type="number"
            value={values.quantity}
            error={errors.quantity}
            onChange={onChange}
            placeholder="Enter quantity"
            min="0"
            step={getPurchaseOrderQuantityStep(values.stockUnit)}
          />
          <FormSelect
            label="Seaweed Type"
            name="seaweedType"
            value={values.seaweedType}
            error={errors.seaweedType}
            onChange={onChange}
            options={seaweedTypeOptions}
            placeholder="Select seaweed type"
            disabled={!seaweedTypeOptions.length}
          />
          <CustomDatePicker
            label="P.O. Date"
            name="poDate"
            value={values.poDate}
            error={errors.poDate}
            onChange={onChange}
          />
          <CustomDatePicker
            label="Delivery Date (Optional)"
            name="deliveryDate"
            value={values.deliveryDate}
            error={errors.deliveryDate}
            onChange={onChange}
          />
          <FormInput
            label="Initial Price per Kilo"
            name="price"
            type="number"
            value={values.price}
            error={errors.price}
            onChange={onChange}
            placeholder="Enter price"
            min="0"
            step="0.01"
          />
        </div>

        <div className="mt-3 grid gap-1.5 rounded-[16px] border border-[var(--theme-border-soft)] bg-[var(--theme-surface-muted)] px-3 py-2.5">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[0.74rem] font-medium text-[var(--theme-text-subtle)]">Status</span>
            <div className="inline-flex rounded-full border border-[var(--theme-border-soft)] bg-[#f7f2fb] px-2.5 py-1 text-[0.76rem] font-medium text-[#6a5bb6]">
              {statusOptions.find((option) => option.value === values.status)?.label || 'Initial'}
            </div>
          </div>
          <span className="text-[0.74rem] text-[var(--theme-text-muted)]">
            New purchase orders start at Initial. Use the progress action later to move them forward.
          </span>
        </div>
      </section>

      <div className="mt-8 flex flex-wrap justify-end gap-3">
        <button
          className="inline-flex min-h-[46px] items-center justify-center rounded-[14px] border border-[var(--theme-border)] bg-white px-7 text-[0.95rem] font-semibold text-[var(--theme-text-subtle)] shadow-[0_6px_14px_rgba(var(--theme-shadow-rgb),0.05)] outline-none transition hover:bg-[var(--theme-surface-soft)] focus-visible:ring-2 focus-visible:ring-[var(--theme-focus)] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
          type="button"
          onClick={onCancel}
          disabled={isSaving}
        >
          Cancel
        </button>
        <button
          className="inline-flex min-h-[46px] items-center justify-center rounded-[14px] bg-[var(--theme-text-subtle)] px-7 text-[0.95rem] font-semibold text-white shadow-[0_10px_22px_rgba(var(--theme-shadow-rgb),0.18)] outline-none transition hover:bg-[var(--theme-accent-strong)] focus-visible:ring-2 focus-visible:ring-[var(--theme-focus)] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-[#d8cec4] disabled:text-[var(--theme-text-muted)] disabled:shadow-none"
          type="submit"
          disabled={isSaving || !canCreatePurchaseOrder}
        >
          {isSaving ? 'Saving...' : 'Save P.O.'}
        </button>
      </div>
    </form>
  );
}

function FormInput({
  label,
  name,
  value,
  error,
  onChange,
  type = 'text',
  placeholder,
  min,
  step,
}) {
  return (
    <label className="grid gap-1.5">
      <span className="text-[0.74rem] font-medium text-[var(--theme-text-subtle)]">{label}</span>
      <input
        className={`min-h-[40px] rounded-full border bg-[var(--theme-surface-warm)] px-3 py-2 text-[0.84rem] outline-none transition placeholder:text-[#b3a79c] ${
          error
            ? 'border-[#d78b7d] focus:border-[#c76552]'
            : 'border-[var(--theme-border)] focus:border-[var(--theme-text-subtle)]'
        }`}
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        min={min}
        step={step}
        inputMode={type === 'number' ? 'decimal' : undefined}
      />
      {error ? <span className="text-[0.74rem] text-[#bb5745]">{error}</span> : null}
    </label>
  );
}

function FormSelect({
  label,
  name,
  value,
  error,
  onChange,
  options,
  placeholder,
  disabled,
}) {
  return (
    <CustomSelect
      label={label}
      name={name}
      value={value}
      error={error}
      onChange={onChange}
      options={options}
      placeholder={placeholder}
      disabled={disabled}
    />
  );
}

export default PurchaseOrderForm;
